import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MatchRing, ConfidenceBadge } from "@/components/app/MatchScore";
import {
  MapPin, Briefcase, ArrowRight, Sparkle, BookmarkSimple, EyeSlash,
  CheckCircle, Clock, Globe, LinkedinLogo, NavigationArrow, Buildings,
  Student, MagnifyingGlass, Fire, Lightning
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days <= 7) return `${days}d ago`;
    if (days <= 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  } catch {
    return null;
  }
}

function freshnessLabel(score) {
  if (score == null) return null;
  if (score >= 90) return { label: "Just posted", color: "text-emerald-600 bg-emerald-50 border-emerald-200" };
  if (score >= 75) return { label: "This week", color: "text-emerald-600 bg-emerald-50 border-emerald-200" };
  if (score >= 55) return { label: "2 weeks ago", color: "text-amber-600 bg-amber-50 border-amber-200" };
  if (score >= 35) return { label: "This month", color: "text-amber-600 bg-amber-50 border-amber-200" };
  if (score >= 15) return { label: "2 months ago", color: "text-slate-500 bg-slate-100 border-slate-200" };
  return { label: "Older", color: "text-slate-400 bg-slate-100 border-slate-200" };
}

function sourceIcon(source) {
  const s = (source || "").toLowerCase();
  if (s.includes("linkedin")) return <LinkedinLogo weight="fill" />;
  if (s.includes("indeed")) return <MagnifyingGlass weight="bold" />;
  if (s.includes("naukri")) return <NavigationArrow weight="bold" />;
  if (s.includes("internshala")) return <Student weight="bold" />;
  if (s.includes("company") || s.includes("career")) return <Buildings weight="bold" />;
  if (s.includes("google") || s.includes("web")) return <Globe weight="bold" />;
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function JobCard({ job, onOpen, onStateChange }) {
  const score = job.analysis?.match_score ?? job.prescore ?? null;
  const ai = !!job.analysis;
  const confidence = job.match_confidence || (ai ? (score >= 70 ? "high" : score >= 40 ? "medium" : "low") : null);
  const matchedSkills = job.matched_skills || job.analysis?.skill_matches || [];
  const age = timeAgo(job.publication_date);
  const srcIcon = sourceIcon(job.source);
  const freshness = freshnessLabel(job.freshness_score);
  const isHot = job.freshness_score != null && job.freshness_score >= 90;

  const handleAction = async (e, endpoint) => {
    e.stopPropagation();
    try {
      await api.post(`/jobs/${endpoint}`, { job });
      if (onStateChange) onStateChange();
      toast.success(
        endpoint === "save" ? "Saved status updated" :
        endpoint === "hide" ? "Job hidden" :
        "Marked as applied"
      );
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div
      data-testid={`job-card-${job.id}`}
      className="group relative flex flex-col h-full border border-slate-200/60 bg-white/80 backdrop-blur-sm rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={() => onOpen(job)}
    >
      {/* Confidence color bar */}
      <div className={`h-0.5 w-full ${
        confidence === "high" ? "bg-emerald-500" :
        confidence === "medium" ? "bg-amber-400" :
        confidence === "low" ? "bg-red-400" : "bg-border"
      }`} />

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Source & Status badges */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="tick-label inline-flex items-center gap-1">
                {srcIcon && <span className="text-xs">{srcIcon}</span>}
                {job.publisher || job.source || "Role"}
              </span>

              {ai ? (
                <Badge variant="outline" className="rounded-full border-brand/30 bg-brand/5 text-brand text-[10px] uppercase tracking-wider font-semibold px-2 py-0">
                  <Sparkle size={10} weight="fill" className="mr-1" />AI Matched
                </Badge>
              ) : confidence ? (
                <ConfidenceBadge confidence={confidence} />
              ) : (
                <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-50 text-emerald-600 text-[10px] uppercase tracking-wider px-2 py-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse mr-1" /> Live
                </Badge>
              )}

              {isHot && (
                <Badge variant="outline" className="rounded-full border-orange-400/40 bg-orange-50 text-orange-600 text-[10px] uppercase tracking-wider px-2 py-0">
                  <Fire size={10} weight="fill" className="mr-1" />Hot
                </Badge>
              )}

              {job.is_saved && (
                <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-50 text-emerald-600 text-[10px] px-2 py-0">
                  Saved
                </Badge>
              )}
              {job.is_applied && (
                <Badge variant="outline" className="rounded-full border-blue-500/30 bg-blue-50 text-blue-600 text-[10px] px-2 py-0">
                  Applied
                </Badge>
              )}
            </div>

            {/* Title */}
            <h3
              className="font-display font-bold text-xl leading-tight tracking-tight line-clamp-2"
              data-testid={`job-title-${job.id}`}
            >
              {job.title}
            </h3>
            <p className="mt-1 text-sm text-ink-soft font-medium">{job.company_name}</p>
          </div>

          {/* Score ring */}
          <MatchRing score={score} size={64} stroke={5} testid={`match-ring-${job.id}`} />
        </div>

        {/* Meta row */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-soft">
          {job.candidate_required_location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={12} weight="bold" />{job.candidate_required_location}
            </span>
          )}
          {job.job_type && (
            <span className="inline-flex items-center gap-1.5">
              <Briefcase size={12} weight="bold" />{job.job_type.replace(/_/g, " ")}
            </span>
          )}
          {/* Freshness label — prioritize over generic timeAgo */}
          {freshness ? (
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${freshness.color}`}>
              <Lightning size={9} weight="fill" />{freshness.label}
            </span>
          ) : age ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} weight="bold" />{age}
            </span>
          ) : null}
          {job.salary && <span className="font-mono">{job.salary}</span>}
        </div>

        {/* Match reason */}
        <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-2 border-l-2 border-brand pl-3">
          {ai && job.analysis?.match_reason ? job.analysis.match_reason : job.match_explanation}
        </p>

        {/* Matched skills */}
        {matchedSkills.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {matchedSkills.slice(0, 4).map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/50 px-2.5 py-1 rounded-md">
                {s}
              </span>
            ))}
            {matchedSkills.length > 4 && (
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 px-2.5 py-1 rounded-md text-ink-soft">
                +{matchedSkills.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Tags fallback */}
        {matchedSkills.length === 0 && (job.tags || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {job.tags.slice(0, 4).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 px-2.5 py-1 rounded-md text-ink-soft">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="px-5 pb-4 pt-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <div className="flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={(e) => handleAction(e, "save")}
            className={`h-8 w-8 rounded-full hover:bg-slate-200/50 ${job.is_saved ? "text-emerald-600" : "text-ink-soft hover:text-ink"}`}
            title="Save job"
          >
            <BookmarkSimple size={16} weight={job.is_saved ? "fill" : "bold"} />
          </Button>
          {!job.is_applied && (
            <Button
              size="icon"
              variant="ghost"
              onClick={(e) => handleAction(e, "hide")}
              className="h-8 w-8 rounded-full hover:bg-red-50 text-ink-soft hover:text-red-600"
              title="Hide job"
            >
              <EyeSlash size={16} weight="bold" />
            </Button>
          )}
          {!job.is_applied && (
            <Button
              size="icon"
              variant="ghost"
              onClick={(e) => handleAction(e, "applied")}
              className="h-8 w-8 rounded-full hover:bg-blue-50 text-ink-soft hover:text-blue-600"
              title="Mark applied"
            >
              <CheckCircle size={16} weight="bold" />
            </Button>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="rounded-full uppercase tracking-wider text-xs h-8 px-4 text-ink bg-white shadow-sm border border-slate-200 hover:bg-slate-50 hover:text-brand"
          data-testid={`open-job-${job.id}`}
        >
          Open <ArrowRight size={12} weight="bold" className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
